// The one local record: his shared total, his best time, his streak, his
// topics. Never leaves the device.
//
// `store.js` gives every entity a list with add/save/remove. A profile is not
// a list — there is exactly one — so this wraps that shape into a singleton
// and is the only thing in the app that writes to it.

import { computed } from 'vue';
import { profiles, addProfile, saveProfile, profilesReady } from '../store.js';
import { ALL_TOPIC_IDS } from './topics.js';

const DEFAULTS = {
  totalScore: 0,
  bestEnduranceMs: 0,
  dailyStreak: 0,
  lastDailyOn: '',
  topics: JSON.stringify(ALL_TOPIC_IDS),
  difficulty: 'warm-up',
  sound: true,
  haptics: true,
  // Time pressure is a setting, not a fact about the game. See game/pace.js.
  timed: true,
  timeScale: 1,
  // A correct answer buys a life back. See game/lives.js.
  regenLives: false,
};

let ensuring = null;

/** Create the single profile row if this is the first ever launch. */
export function ensureProfile() {
  if (!ensuring) {
    ensuring = profilesReady
      .then(() => {
        if (!profiles.value.length) addProfile({ ...DEFAULTS });
        return profiles.value[0];
      })
      .catch(() => null);
  }
  return ensuring;
}

/** Always readable, even before the store has hydrated. */
export const profile = computed(() => profiles.value[0] ?? { id: 0, ...DEFAULTS });

export function updateProfile(patch) {
  const row = profiles.value[0];
  if (!row) return null;
  const next = { ...row, ...patch };
  saveProfile(next);
  return next;
}

/**
 * The topic pools switched on.
 *
 * Parsed defensively because `topics` is a text column: the /demo fixture puts
 * a sentence in it, and a JSON.parse that throws on the demo route would take
 * every mode screen down with it. An unreadable value means "all topics",
 * which is also the correct default for a fresh install.
 */
export const enabledTopics = computed(() => {
  try {
    const parsed = JSON.parse(profile.value.topics ?? '[]');
    return Array.isArray(parsed) && parsed.length ? parsed : ALL_TOPIC_IDS;
  } catch {
    return ALL_TOPIC_IDS;
  }
});

export const difficultyFloor = computed(() => profile.value.difficulty || 'warm-up');

/** The draw settings every mode screen passes straight to the engine. */
export const drawOptions = computed(() => ({
  topics: enabledTopics.value,
  difficulty: difficultyFloor.value,
}));

export function addToTotal(points) {
  return updateProfile({ totalScore: (profile.value.totalScore ?? 0) + points });
}
